// Saved-card API + the "save a card" flow, shared by the billing settings page
// and the subscribe confirmation modal.
//
// Saving a card is three steps and all three matter: a SetupIntent from our API,
// a Stripe.js confirmation that never touches our servers with raw card data,
// then persisting only the resulting payment-method id. Keeping the sequence in
// one place means both call sites stay in step.

import { CardElement } from '@stripe/react-stripe-js';
import axiosInstance from './axiosConfig';
import { authHeaders } from './billingApi';

export const fetchPaymentMethods = () =>
  axiosInstance
    .get('payment_methods/', authHeaders())
    .then(({ data }) => (Array.isArray(data) ? data : []));

// Billing charges the customer's Stripe default, so treat that as the card on
// file; fall back to the first saved card if no row is flagged.
export const defaultPaymentMethod = (methods) =>
  (methods || []).find((method) => method.is_default) || (methods || [])[0] || null;

export const setDefaultPaymentMethod = (id) =>
  axiosInstance
    .put(`payment_methods/${id}`, { is_default: true }, authHeaders())
    .then(({ data }) => data);

export const describeCard = (method) =>
  method ? `${(method.brand || 'Card').toUpperCase()} •••• ${method.last_four}` : '';

export const describeExpiry = (method) => {
  if (!method?.exp_month || !method?.exp_year) return '';
  return `Expires ${String(method.exp_month).padStart(2, '0')}/${String(method.exp_year).slice(-2)}`;
};

// Collect and store the card currently in the CardElement.
//
// Returns { method } on success or { cardError } when Stripe rejects the card
// itself (declined, incomplete, bad number). A card error is the user's problem
// to fix in the form, so it is returned rather than thrown — everything else
// (no SetupIntent, our API failing) throws for the caller's catch.
export const confirmAndSaveCard = async ({ stripe, elements, makeDefault = true }) => {
  if (!stripe || !elements) throw new Error('Card entry is not ready yet.');

  const { data } = await axiosInstance.post('payment_methods/setup_intent', {}, authHeaders());
  const clientSecret = data?.client_secret;
  if (!clientSecret) throw new Error('Could not start card setup.');

  const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
    payment_method: { card: elements.getElement(CardElement) },
  });
  if (error) {
    return { cardError: error.message || 'Your card could not be saved.' };
  }

  const { data: method } = await axiosInstance.post(
    'payment_methods/',
    { stripe_payment_method_id: setupIntent.payment_method, is_default: makeDefault },
    authHeaders(),
  );
  return { method };
};
